import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink } from "react-router-dom";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  ListItem,
  ListItemButton,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import PushPinIcon from "@mui/icons-material/PushPin";

import { InternalRedirection, Option, SubMenuName } from "../interface";
import {
  extractRoutes,
  formatConfigurationJson,
  getSubMenuOptions,
  getUrl,
  withHardcodedRoutes,
} from "./utils";
import { ADMIN_REVBELL_SUBMENU_HIERARCHY } from "./menusStructure";
import { getCurrentLocale, Locale } from "../../Locale";
import { BetaChip } from "../../components/BetaChip";
import {
  currentUserAtom,
  menuItemsAtom,
  Roles,
  selectedCustomerAtom,
  useHasRole,
} from "../../AuthProvider";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { GenericObject } from "../../services/Price";
import { getMenuData, PINNED_WINDOW_WIDTH_MIN_SIZE, Row } from "../utils";
import { setCookie } from "../../services/utils";
import { favoritesAtom } from "../atom";
import FavoriteButton from "./FavoriteButton";
import { useWindowWidth } from "../../hooks/window";

export const menuAtom = atom<{
  pinned: boolean;
  collapseState: GenericObject;
}>({
  key: "revbell-menu",
  default: getMenuData(),
});

const useStyles = makeStyles()((theme) => ({
  menu: {
    "& .MuiTypography-root": {
      color: theme.vars.palette.menu.text,
    },
    "&:hover .MuiTypography-root": {
      color: theme.vars.palette.text.accentInverted,
    },
    "&.active p": {
      color: theme.vars.palette.text.accentInverted,
    },
    "&.disabled": {
      "& .MuiTypography-root": {
        color: theme.vars.palette.text.disabled,

        "&:hover:": {
          color: theme.vars.palette.text.disabled,
        },
      },
    },
  },
  chip: {
    height: "20px",
    backgroundColor: theme.vars.palette.error.main,
    color: theme.vars.palette.common.white,
  },
}));

const stripTrailingSlash = (path: string): string =>
  path.length > 1 ? path.replace(/\/+$/, "") : path;

const newChip = (option: Option) =>
  option.type === "internalRedirection" && option.isNew ? (
    <BetaChip dense label="New" />
  ) : null;

const isActiveSubMenu = (
  menu: InternalRedirection,
  siblingRoutes: string[]
): boolean => {
  if (!menu.redirectionTo) return false;

  const currentPath = stripTrailingSlash(window.location.pathname);
  const targetPath = stripTrailingSlash("/" + menu.redirectionTo);

  const matches = (route: string) =>
    currentPath === route || currentPath.startsWith(route + "/");

  if (!matches(targetPath)) return false;

  // Most-specific wins: a parent route (e.g. "/reporting") must not stay active
  // when a longer sibling route (e.g. "/reporting/booking") also matches the
  // current path.
  return !siblingRoutes.some(
    (route) =>
      route !== targetPath && route.length > targetPath.length && matches(route)
  );
};

const ItemContainer = ({
  children,
  option,
  locale,
  classes,
  sx,
  siblingRoutes,
}: {
  children: React.ReactNode;
  option: Option;
  locale: Locale;
  classes?: string;
  sx?: SxProps<Theme>;
  siblingRoutes: string[];
}) => {
  const { classes: styles } = useStyles();

  if (option.type === "externalRedirection") {
    return (
      <ListItemButton
        rel="noopener noreferrer"
        target={"_blank"}
        component={NavLink}
        to={getUrl(locale, option)}
        data-key={option.value}
        sx={sx}
        className={`${classes} ${styles.menu}`}
      >
        {children}
      </ListItemButton>
    );
  } else if (option.type === "internalRedirection" && !option.redirectionTo) {
    return (
      <Box data-key={option.value} sx={sx} className={classes}>
        {children}
      </Box>
    );
  }

  const isActive = isActiveSubMenu(option, siblingRoutes);

  return (
    <ListItemButton
      target={"_self"}
      component={NavLink}
      to={option.redirectionTo ? option.redirectionTo : ""}
      data-key={option.value}
      sx={sx}
      className={`${classes} ${styles.menu} ${isActive ? "active" : ""}`}
    >
      {children}
    </ListItemButton>
  );
};

const SubMenu = ({
  activeItem,
  adminMenus,
  reportingMenus,
  unmappedAmount,
}: {
  activeItem: {
    name: string;
    translatedName?: string;
  };
  adminMenus: InternalRedirection[];
  reportingMenus: InternalRedirection[];
  unmappedAmount?: GenericObject;
}) => {
  const intl = useIntl();
  const { classes } = useStyles();
  const { hasRole } = useHasRole();
  const currentUser = useRecoilValue(currentUserAtom);
  const selectedCustomer = useRecoilValue(selectedCustomerAtom);
  const windowWidth = useWindowWidth();
  const isStaf = useMemo(() => hasRole(Roles.SuperAdmin), [hasRole]);
  const favorites = useRecoilValue(favoritesAtom);
  const menuItems = useRecoilValue(menuItemsAtom);

  const accessibleRoutes = useMemo(
    () =>
      withHardcodedRoutes(
        new Set([
          ...menuItems.flatMap((m) =>
            [m.reactRoute, m.urlPath].filter((r): r is string => !!r)
          ),
          ...extractRoutes(
            formatConfigurationJson(currentUser, selectedCustomer)
          ),
          ...(isStaf ? extractRoutes(ADMIN_REVBELL_SUBMENU_HIERARCHY) : []),
        ])
      ),
    [menuItems, currentUser, isStaf, selectedCustomer]
  );

  const subMenuOptions = useMemo(
    () =>
      getSubMenuOptions(
        activeItem,
        adminMenus,
        reportingMenus,
        currentUser,
        isStaf,
        favorites,
        accessibleRoutes,
        selectedCustomer
      ),
    [
      activeItem,
      adminMenus,
      reportingMenus,
      currentUser,
      isStaf,
      favorites,
      accessibleRoutes,
      selectedCustomer,
    ]
  );
  // All redirection targets shown in this submenu (parents + nested), used by
  // isActiveSubMenu to keep only the most-specific match highlighted.
  const submenuRoutes = useMemo(() => {
    const routes: string[] = [];
    const collect = (options: Option[]) => {
      options.forEach((option) => {
        if (
          option.type === "internalRedirection" &&
          option.redirectionTo &&
          option.redirectionTo !== "#"
        ) {
          routes.push(stripTrailingSlash("/" + option.redirectionTo));
        }
        if (option.nestedOptions) collect(option.nestedOptions);
      });
    };
    collect(subMenuOptions);
    return routes;
  }, [subMenuOptions]);

  const [menu, setMenu] = useRecoilState(menuAtom);
  const [pinned, setPinned] = useState(menu.pinned);
  const [collapseState, setCollapseState] = useState<Record<number, boolean>>(
    menu.collapseState?.[activeItem.name] ||
      subMenuOptions.reduce((acc, _, i) => ({ ...acc, [i]: false }), {})
  );
  const [locale] = useState<Locale>(() => getCurrentLocale());

  // Synchronize collapseState when the active item changes to ensure that when writing the atom and cookie we change the correct values
  useEffect(() => {
    setCollapseState(
      menu.collapseState?.[activeItem.name] ||
        subMenuOptions.reduce((acc, _, i) => ({ ...acc, [i]: false }), {})
    );
  }, [activeItem.name, menu.collapseState, subMenuOptions]);

  // Updates the global menu and cookie with a small delay to avoid stray writes
  useEffect(() => {
    const timeout = setTimeout(() => {
      setMenu((prev) => {
        const newMenuState = {
          ...prev,
          pinned,
          collapseState: {
            ...prev.collapseState,
            [activeItem.name]: collapseState,
          },
        };

        // Ensure something change
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(newMenuState);
        if (prevStr !== newStr) {
          setCookie("revbell-menu", newStr);
          return newMenuState;
        }

        return prev;
      });
    }, 120);

    return () => clearTimeout(timeout);
  }, [pinned, collapseState, activeItem.name, setMenu]);

  return (
    <Box sx={{ py: 1 }}>
      <Row
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mx: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "1rem",
            p: "8px 0",
            color: (theme) => theme.vars.palette.menu.text,
          }}
          component={"h2"}
        >
          {activeItem.translatedName ||
            intl.formatMessage({
              id: activeItem.name,
            })}
        </Typography>
        {windowWidth > PINNED_WINDOW_WIDTH_MIN_SIZE && (
          <Box
            sx={(theme) => ({
              width: "24px",
              height: "24px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: pinned
                ? `${theme.vars.palette.background.inverted}`
                : "unset",
              borderRadius: pinned ? "6px" : "unset",
            })}
            onClick={() => setPinned((prev) => !prev)}
          >
            <PushPinIcon
              sx={(theme) => ({
                fontSize: "1rem",
                verticalAlign: "middle",
                color: pinned
                  ? theme.vars.palette.text.accentInverted
                  : theme.vars.palette.menu.text,
              })}
            />
          </Box>
        )}
      </Row>
      <Divider
        sx={(theme) => ({
          borderColor: theme.vars.palette.background.inverted,
        })}
      />
      {activeItem.name === SubMenuName.DEFAULT ? (
        <Typography
          component={"p"}
          sx={(theme) => ({
            color: theme.vars.palette.menu.text,
            whiteSpace: "normal",
            pt: 1,
            px: 2,
            pb: 0,
            fontSize: "0.875rem",
          })}
        >
          <FormattedMessage id="DEFAULT_MENU_TEXT" />
        </Typography>
      ) : (
        subMenuOptions.map((option, index) => {
          // Display menus without collapsed menu level
          if (option.menuLevel === null && option.nestedOptions) {
            return option.nestedOptions.map((subOption, index) => {
              const favoriteId =
                subOption.type === "internalRedirection" &&
                subOption.redirectionTo &&
                subOption.redirectionTo !== "#"
                  ? subOption.redirectionTo
                  : subOption.type === "externalRedirection"
                  ? subOption.urlAll
                  : null;

              const label = subOption.toTranslate
                ? intl.formatMessage({ id: subOption.value })
                : subOption.value;
              const isFavorite =
                favoriteId !== null &&
                favorites.some((f) => f.id === favoriteId);

              return (
                <ItemContainer
                  key={`${subOption.value}-${index}`}
                  option={subOption}
                  locale={locale}
                  siblingRoutes={submenuRoutes}
                  data-key={subOption.value}
                  classes={subOption.isDisable ? " disabled" : ""}
                  sx={{
                    py: 0.5,
                    px: 2,
                    "& .fav-hidden": { visibility: "hidden" },
                    "&:hover .fav-hidden": { visibility: "visible" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      alignItems: "center",
                      paddingLeft: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={(theme) => ({
                          color: theme.vars.palette.menu.text,
                          fontSize: "0.875rem",
                        })}
                      >
                        {label}
                      </Typography>
                      {newChip(subOption)}
                    </Box>
                    {favoriteId && !subOption.isDisable && (
                      <Box className={isFavorite ? undefined : "fav-hidden"}>
                        <FavoriteButton id={favoriteId} label={label} />
                      </Box>
                    )}
                  </Box>
                  {activeItem.name === SubMenuName.CONFIGURATION &&
                    unmappedAmount &&
                    subOption.name &&
                    !!unmappedAmount[subOption.name] && (
                      <Chip
                        size="small"
                        className={classes.chip}
                        label={
                          Number(unmappedAmount[subOption.name]) > 99
                            ? "+99"
                            : unmappedAmount[subOption.name]
                        }
                      />
                    )}
                </ItemContainer>
              );
            });
          } else if (option.nestedOptions && option.nestedOptions.length > 0) {
            return (
              <Box key={index}>
                <ListItem
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 0,
                    pr: 2,
                    pb: 0,
                    pl: 0,
                    "&:hover": {
                      backgroundColor:
                        option.type === "internalRedirection" &&
                        !option.redirectionTo
                          ? "unset"
                          : theme.vars.palette.action.hover,
                    },
                  })}
                >
                  <ItemContainer
                    key={option.value}
                    option={option}
                    locale={locale}
                    siblingRoutes={submenuRoutes}
                    sx={{
                      pt: 1,
                      pr: 0,
                      pb: 1,
                      pl: 2,
                      width: "100%",
                      "&:hover": {
                        backgroundColor: "unset",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={(theme) => ({
                          fontWeight: "bold",
                          color: theme.vars.palette.menu.text,
                          fontSize: "0.875rem",
                        })}
                      >
                        {intl.formatMessage({ id: option.value })}
                      </Typography>
                    </Box>
                  </ItemContainer>
                  {option.nestedOptions &&
                    (collapseState[index] ? (
                      <ExpandLessIcon
                        sx={(theme) => ({
                          cursor: "pointer",
                          color: theme.vars.palette.menu.text,
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCollapseState((prev) => ({
                            ...prev,
                            [index]: !prev[index],
                          }));
                        }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        sx={(theme) => ({
                          cursor: "pointer",
                          color: theme.vars.palette.menu.text,
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCollapseState((prev) => ({
                            ...prev,
                            [index]: !prev[index],
                          }));
                        }}
                      />
                    ))}
                </ListItem>
                <Divider
                  sx={(theme) => ({
                    width: "80%",
                    margin: "auto",
                    borderColor: theme.vars.palette.background.inverted,
                  })}
                />
                {option.nestedOptions && (
                  <Collapse
                    in={collapseState[index]}
                    appear={false}
                    timeout="auto"
                    unmountOnExit
                  >
                    {option.nestedOptions.map((subOption, index) => {
                      const favoriteId =
                        subOption.type === "internalRedirection" &&
                        subOption.redirectionTo &&
                        subOption.redirectionTo !== "#"
                          ? subOption.redirectionTo
                          : subOption.type === "externalRedirection"
                          ? subOption.urlAll
                          : null;

                      const label = subOption.toTranslate
                        ? intl.formatMessage({ id: subOption.value })
                        : subOption.value;
                      const isFavorite =
                        favoriteId !== null &&
                        favorites.some((f) => f.id === favoriteId);

                      return (
                        <ItemContainer
                          key={`${subOption.value}-${index}`}
                          option={subOption}
                          locale={locale}
                          siblingRoutes={submenuRoutes}
                          data-key={subOption.value}
                          classes={subOption.isDisable ? " disabled" : ""}
                          sx={{
                            py: 0.5,
                            px: 2,
                            "& .fav-hidden": { visibility: "hidden" },
                            "&:hover .fav-hidden": { visibility: "visible" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              alignItems: "center",
                              paddingLeft: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                sx={(theme) => ({
                                  color: theme.vars.palette.menu.text,
                                  fontSize: "0.875rem",
                                })}
                              >
                                {label}
                              </Typography>
                              {newChip(subOption)}
                            </Box>
                            {favoriteId && !subOption.isDisable && (
                              <Box
                                className={
                                  isFavorite ? undefined : "fav-hidden"
                                }
                              >
                                <FavoriteButton id={favoriteId} label={label} />
                              </Box>
                            )}
                          </Box>
                          {activeItem.name === SubMenuName.CONFIGURATION &&
                            unmappedAmount &&
                            subOption.name &&
                            !!unmappedAmount[subOption.name] && (
                              <Chip
                                size="small"
                                className={classes.chip}
                                label={
                                  Number(unmappedAmount[subOption.name]) > 99
                                    ? "+99"
                                    : unmappedAmount[subOption.name]
                                }
                              />
                            )}
                        </ItemContainer>
                      );
                    })}
                  </Collapse>
                )}
              </Box>
            );
          }

          return null;
        })
      )}
    </Box>
  );
};

export default SubMenu;
